<template>
  <VGrid class="v-form-builder" :gap="15">
    <VCol v-if="blueprint.settings.showSectionsSidebar" :md="7">
      <VFormBuilderSections :blueprint editable />
    </VCol>

    <VCol :md="blueprint.settings.showSectionsSidebar ? 17 : 24">
      <Transition mode="out-in" name="v-fade-fast">
        <div :key="blueprint.activeSection.id">
          <VFormBuilderSection :blueprint />
          <div v-if="blueprint.visibleQuestions.length === 0" class="v-form-builder__empty-message">
            {{ i18n.startByAddingYourFirstQuestion }}
          </div>
          <TransitionGroup v-else class="v-form-builder__questions" name="v-list" tag="div">
            <VFormBuilderQuestion v-for="question in blueprint.visibleQuestions" :key="question.id" :blueprint :question-id="question.id">
              <template #question-header-right="slotProps">
                <slot name="question-header-right" v-bind="slotProps" />
              </template>
              <template #question-header-bottom="slotProps">
                <slot name="question-header-bottom" v-bind="slotProps" />
              </template>
              <template #answer="slotProps">
                <slot name="answer" v-bind="slotProps" />
              </template>
              <template #answer-label="slotProps">
                <slot name="answer-label" v-bind="slotProps" />
              </template>
              <template #dropdown-option="slotProps">
                <slot name="dropdown-option" v-bind="slotProps" />
              </template>
              <template #true-false-option="slotProps">
                <slot name="true-false-option" v-bind="slotProps" />
              </template>
            </VFormBuilderQuestion>
          </TransitionGroup>
        </div>
      </Transition>

      <VFormBuilderSelectQuestion :blueprint :button-label="i18n.addQuestion" scroll-question-into-view-on-select @select="onSelectQuestionType" />
    </VCol>
  </VGrid>
</template>

<script setup lang="ts">
import { nextTick } from 'vue'

import { i18nFormBuilder as i18n } from '../../utils/i18n.ts'
import VCol from '../VCol.vue'
import VGrid from '../VGrid.vue'
import { type Blueprint, type DropdownQuestion, type Question, type QuestionOption, type QuestionType, type TrueFalseQuestion } from './useBlueprint.ts'
import VFormBuilderQuestion from './VFormBuilderQuestion.vue'
import VFormBuilderSection from './VFormBuilderSection.vue'
import VFormBuilderSections from './VFormBuilderSections.vue'
import VFormBuilderSelectQuestion from './VFormBuilderSelectQuestion.vue'

interface Props {
  blueprint: Blueprint
}

const props = defineProps<Props>()

defineSlots<{
  answer(props: { question: Question }): any
  'answer-label'(props: { question: Question }): any
  'question-header-right'(props: { question: Question }): any
  'question-header-bottom'(props: { question: Question }): any
  'true-false-option'(props: { question: TrueFalseQuestion; option: QuestionOption }): any
  'dropdown-option'(props: { question: DropdownQuestion; option: QuestionOption }): any
}>()

async function onSelectQuestionType(questionType: QuestionType) {
  const question = await props.blueprint.addQuestion(questionType)

  await nextTick()
  const el = document.querySelector(`#v-form-builder-question-${question.id}`)
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}
</script>

<style>
.v-form-builder {
  margin-bottom: 30px;
  min-width: 480px;
}

.v-form-builder__empty-message {
  color: var(--color-grey-60);
  text-align: center;
  margin-bottom: 50px;
}
</style>
